import { CommonModule } from '@angular/common';
import {
	Component,
	OnInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef
} from '@angular/core';
import {
	FormArray,
	FormBuilder,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	Validators
} from '@angular/forms';
import { SharedModule } from '@app/shared/shared.module';
import { finalize } from 'rxjs';
import { ActivityService } from '../services/activity.service';
import {
	ConfirmDialogComponent,
	ConfirmDialogModel
} from '@app/shared/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';

interface Activity {
	Activity: string;
	Description: string;
	IsDeleted: boolean;
	selected?: boolean;
}

interface WorkOrder {
	CityworksWorkOrderID: number;
	CityworksWOID: number;
	IsDeleted: boolean;
	IsAdd?: boolean;
}

@Component({
	selector: 'app-citiworks-admin',
	imports: [CommonModule, FormsModule, ReactiveFormsModule, SharedModule],
	templateUrl: './citiworks-admin.component.html',
	styleUrl: './citiworks-admin.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class CitiworksAdminComponent implements OnInit {
	citiworksForm!: FormGroup;
	citiworksFormSubmitted: boolean = false;
	activities: Activity[] = [];
	workOrders: WorkOrder[] = [];
	filteredWorkOrders: WorkOrder[] = [];
	activitiesLoading = true;
	workOrdersLoading = true;
	isUpdating = false;
	duplicateError = false;
	searchTerm: string = '';

	constructor(
		private readonly fb: FormBuilder,
		private readonly activityService: ActivityService,
		private readonly dialogRef: MatDialog,
		private readonly toastr: ToastrService,
		private readonly cdr: ChangeDetectorRef // Add ChangeDetectorRef
	) {}

	ngOnInit(): void {
		this.citiworksForm = this.fb.group({
			activities: this.fb.array([]),
			newWorkOrder: [
				'',
				[Validators.required, Validators.pattern(/^\d{1,6}$/)]
			],
			searchWorkOrder: ['']
		});

		// Subscribe to search input changes
		this.citiworksForm
			.get('searchWorkOrder')
			?.valueChanges.subscribe((value) => {
				this.searchTerm = value || '';
				this.filterWorkOrders();
			});

		this.fetchActivities();
		this.fetchWorkOrders();
	}

	get activitiesArray(): FormArray {
		return this.citiworksForm.get('activities') as FormArray;
	}

	fetchActivities(): void {
		this.activitiesLoading = true;
		const payload = {
			filters: [],
			Sortings: [],
			Start: 0,
			Length: -1
		};
		this.activityService
			.getCityworksActivities(payload)
			.pipe(
				finalize(() => {
					this.activitiesLoading = false;
					this.cdr.markForCheck(); // Trigger change detection
				})
			)
			.subscribe({
				next: (response: any) => {
					this.activities = response.Data.sort(
						(a: Activity, b: Activity) =>
							a.Activity.localeCompare(b.Activity)
					);
					this.initializeActivitiesForm();
					this.cdr.markForCheck(); // Trigger change detection
				},
				error: (error) => {
					console.error('Error fetching activities:', error);
					this.cdr.markForCheck();
				}
			});
	}

	fetchWorkOrders(): void {
		this.workOrdersLoading = true;
		const payload = {
			filters: [],
			Sortings: [],
			Start: 0,
			Length: -1
		};
		this.activityService
			.getCityworksWorkOrders(payload)
			.pipe(
				finalize(() => {
					this.workOrdersLoading = false;
					this.cdr.markForCheck(); // Trigger change detection
				})
			)
			.subscribe({
				next: (response: any) => {
					this.workOrders = response.Data.filter(
						(workOrder: WorkOrder) => workOrder.IsDeleted === false
					).sort(
						(a: WorkOrder, b: WorkOrder) =>
							a.CityworksWOID - b.CityworksWOID
					);
					this.filterWorkOrders();
					this.cdr.markForCheck(); // Trigger change detection
				},
				error: (error) => {
					console.error('Error fetching Cityworks IDs:', error);
					this.cdr.markForCheck();
				}
			});
	}

	initializeActivitiesForm(): void {
		while (this.activitiesArray.length !== 0) {
			this.activitiesArray.removeAt(0);
		}

		this.activities.forEach((activity) => {
			this.activitiesArray.push(
				this.fb.control(activity.IsDeleted === false)
			);
		});
	}

	onActivityToggle(index: number): void {
		if (this.isUpdating) return;

		this.isUpdating = true;
		const activity = this.activities[index];
		const isActive = this.activitiesArray.at(index).value;

		activity.IsDeleted = !isActive;
		this.updateActivityStatus(activity, index);
	}

	updateActivityStatus(activity: Activity, index: number): void {
		const payload = {
			Activity: activity.Activity,
			IsDeleted: activity.IsDeleted
		};

		this.activityService
			.updateCityworksActivity(payload)
			.pipe(
				finalize(() => {
					this.isUpdating = false;
					this.cdr.markForCheck();
				})
			)
			.subscribe({
				next: (response) => {
					this.activityService.updateActivitySubject({ ...activity });
					this.cdr.markForCheck();
				},
				error: (error) => {
					const currentValue = this.activitiesArray.at(index).value;
					this.activitiesArray
						.at(index)
						.setValue(!currentValue, { emitEvent: false });
					this.activities[index].IsDeleted = !activity.IsDeleted;
					this.cdr.markForCheck();
				}
			});
	}

	addWorkOrder(): void {
		this.duplicateError = false;
		this.citiworksFormSubmitted = true;

		const newWorkOrderControl = this.citiworksForm.get('newWorkOrder');
		const newWorkOrderValue = newWorkOrderControl?.value;

		if (newWorkOrderControl?.invalid) {
			if (newWorkOrderControl.errors?.['required']) {
				this.toastr.error('Cityworks ID is required', 'Error', {
					positionClass: 'toast-bottom-left',
					toastClass: 'toast-icon custom-toast-error'
				});
			} else if (newWorkOrderControl.errors?.['pattern']) {
				this.toastr.error(
					'Cityworks ID must be 1 to 6 digits only',
					'Error',
					{
						positionClass: 'toast-bottom-left',
						toastClass: 'toast-icon custom-toast-error'
					}
				);
			}
			this.cdr.markForCheck();
			return;
		}

		if (newWorkOrderValue && newWorkOrderValue.trim()) {
			const woNumber = Number(newWorkOrderValue.trim());

			const isDuplicate = this.workOrders.some(
				(wo) => wo.CityworksWOID === woNumber && !wo.IsDeleted
			);

			if (isDuplicate) {
				this.duplicateError = true;
				this.toastr.error('This Cityworks ID already exists', 'Error', {
					positionClass: 'toast-bottom-left',
					toastClass: 'toast-icon custom-toast-error'
				});
				this.cdr.markForCheck();
				return;
			}

			const newWorkOrder: WorkOrder = {
				CityworksWorkOrderID: 0,
				CityworksWOID: woNumber,
				IsDeleted: false,
				IsAdd: true
			};

			this.activityService
				.saveCityworksWorkOrder(newWorkOrder)
				.subscribe({
					next: (response: any) => {
						const addedOrder = {
							...newWorkOrder,
							CityworksWorkOrderID: response.CityworksWorkOrderID
						};
						this.workOrders.push(addedOrder);
						this.workOrders.sort(
							(a: WorkOrder, b: WorkOrder) =>
								a.CityworksWOID - b.CityworksWOID
						);
						this.filterWorkOrders();
						newWorkOrderControl?.setValue('');
						newWorkOrderControl?.markAsUntouched();

						this.toastr.success(
							'Cityworks ID ADDED successfully',
							'Success',
							{
								positionClass: 'toast-bottom-left',
								toastClass: 'toast-icon custom-toast-success'
							}
						);
						this.citiworksForm.get('newWorkOrder')?.markAsTouched();
						this.citiworksForm
							.get('newWorkOrder')
							?.markAsPristine();
						this.citiworksFormSubmitted = false;
						this.activityService.setCityworksWorkOrderFlag(true);
						this.cdr.markForCheck();
					},
					error: (error) => {
						this.toastr.error(
							'Error adding Cityworks ID',
							'Error',
							{
								positionClass: 'toast-bottom-left',
								toastClass: 'toast-icon custom-toast-error'
							}
						);
						this.cdr.markForCheck();
					}
				});
		}
	}

	removeWorkOrder(order: WorkOrder): void {
		const message = `Are you sure you want to delete this Cityworks ID?`;
		const dialogData = new ConfirmDialogModel('Confirm Delete', message);
		const dialogRef = this.dialogRef.open(ConfirmDialogComponent, {
			maxWidth: '400px',
			data: dialogData
		});
		dialogRef.afterClosed().subscribe((dialogResult) => {
			if (dialogResult) {
				const updatedOrder: WorkOrder = {
					...order,
					IsDeleted: true,
					IsAdd: false
				};

				this.activityService
					.saveCityworksWorkOrder(updatedOrder)
					.subscribe({
						next: (response) => {
							if (response.Result === true) {
								const index = this.workOrders.indexOf(order);
								if (index !== -1) {
									this.workOrders.splice(index, 1);
								}
								this.filterWorkOrders();

								this.toastr.clear();
								this.toastr.success(
									'Cityworks ID DELETED successfully.',
									'Success',
									{
										positionClass: 'toast-bottom-left',
										toastClass:
											'toast-icon custom-toast-success'
									}
								);
								this.activityService.setCityworksWorkOrderFlag(
									true
								);
								this.cdr.markForCheck();
							} else {
								this.toastr.error(response.Message, 'Error', {
									positionClass: 'toast-bottom-left',
									toastClass: 'toast-icon custom-toast-error'
								});
								this.cdr.markForCheck();
							}
						},
						error: (error) => {
							this.toastr.error(
								'Error removing Cityworks ID.',
								'Error',
								{
									positionClass: 'toast-bottom-left',
									toastClass: 'toast-icon custom-toast-error'
								}
							);
							this.cdr.markForCheck();
						}
					});
			}
		});
	}

	getSelectedActivities(): { Activity: string; selected: boolean }[] {
		return this.activities.map((activity, index) => ({
			Activity: activity.Activity,
			selected: this.activitiesArray.at(index).value
		}));
	}

	onSaveCitiworks(): void {
		if (this.citiworksForm.valid) {
			const formData = {
				activities: this.getSelectedActivities(),
				workOrders: this.workOrders
			};
			this.saveAllChanges(formData);
		}
	}

	saveAllChanges(formData: any): void {
		this.activityService.saveCityworksWorkOrder(formData).subscribe({
			next: (response) => {
				console.log('All changes SAVED successfully', response);
				this.cdr.markForCheck();
			},
			error: (error) => {
				console.error('Error saving changes:', error);
				this.cdr.markForCheck();
			}
		});
	}

	filterWorkOrders(): void {
		if (!this.searchTerm || this.searchTerm.trim() === '') {
			this.filteredWorkOrders = [...this.workOrders];
		} else {
			const search = this.searchTerm.trim().toLowerCase();
			this.filteredWorkOrders = this.workOrders.filter((order) =>
				order.CityworksWOID.toString().toLowerCase().includes(search)
			);
		}
		this.cdr.markForCheck();
	}
}
